import React, { useState, useRef, useEffect } from 'react';
import api from '../services/apiClient';
import { useChatStore } from '../state/chatStore';
import type { ChatMessage } from '../state/chatStore';

interface Position {
  x: number;
  y: number;
}

const AionAssistant: React.FC = () => {
  const { isOpen, setIsOpen, messages, addMessage, isTyping, setIsTyping } = useChatStore();
  const [inputMessage, setInputMessage] = useState('');
  
  // Posición del chatbot (fixed al viewport)
  const [position, setPosition] = useState<Position>({ x: 0, y: 0 });
  const [panelPosition, setPanelPosition] = useState<Position>({ x: 0, y: 0 });
  
  // Drag refs
  const isDraggingRef = useRef<boolean>(false);
  const dragStartRef = useRef<Position>({ x: 0, y: 0 });
  const chatPositionRef = useRef<Position>({ x: 0, y: 0 });
  const hasDraggedRef = useRef<boolean>(false); // Para evitar click después de drag
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  
  // Dimensiones del panel
  const PANEL_WIDTH = 384;
  const PANEL_HEIGHT = 500;
  const MARGIN = 16;
  
  // Calcular posición inicial
  useEffect(() => {
    const calculateInitialPosition = () => {
      const initialPos = {
        x: window.innerWidth - 120,  // Botón en esquina inferior derecha
        y: window.innerHeight - 80
      };
      
      setPosition(initialPos);
      chatPositionRef.current = initialPos;
      
      // Calcular posición inicial del panel
      calculatePanelPosition(initialPos);
    };
    
    calculateInitialPosition();
    window.addEventListener('resize', calculateInitialPosition);
    
    return () => window.removeEventListener('resize', calculateInitialPosition);
  }, []);
  
  // Calcular posición del panel basándose en la posición del botón
  const calculatePanelPosition = (buttonPos: Position) => {
    if (!chatRef.current) return buttonPos;
    
    // Obtener posición real del botón
    const rect = chatRef.current.getBoundingClientRect();
    
    // Posición preferida: arriba y alineado a la derecha del botón
    let panelX = buttonPos.x + rect.width - PANEL_WIDTH;
    let panelY = buttonPos.y - PANEL_HEIGHT - 12;
    
    // Clamp horizontal
    panelX = Math.max(MARGIN, Math.min(panelX, window.innerWidth - PANEL_WIDTH - MARGIN));
    
    // Clamp vertical - si no hay espacio arriba, poner abajo
    if (panelY < MARGIN) {
      panelY = buttonPos.y + rect.height + 12;
    }
    
    // Verificar que no se salga por abajo
    panelY = Math.max(MARGIN, Math.min(panelY, window.innerHeight - PANEL_HEIGHT - MARGIN));
    
    const finalPos = { x: panelX, y: panelY };
    setPanelPosition(finalPos);
    return finalPos;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  // ════════════════════════════════════════════════════════════════
  // DRAG HANDLERS - SIMPLE Y FUNCIONAL
  // ════════════════════════════════════════════════════════════════
  
  const handleHeaderMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    
    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY
    };
    
    chatPositionRef.current = { ...position };
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !chatRef.current) return;
      
      const deltaX = moveEvent.clientX - dragStartRef.current.x;
      const deltaY = moveEvent.clientY - dragStartRef.current.y;
      
      // Activar flag de drag si se movió
      if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
        hasDraggedRef.current = true;
      }
      
      // Usar transform para movimiento suave en tiempo real
      chatRef.current.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
    };
    
    const handleMouseUp = (upEvent: MouseEvent) => {
      if (isDraggingRef.current && chatRef.current) {
        const deltaX = upEvent.clientX - dragStartRef.current.x;
        const deltaY = upEvent.clientY - dragStartRef.current.y;
        
        // Calcular nueva posición
        let newX = chatPositionRef.current.x + deltaX;
        let newY = chatPositionRef.current.y + deltaY;
        
        // Obtener dimensiones del botón
        const rect = chatRef.current.getBoundingClientRect();
        const buttonWidth = rect.width;
        const buttonHeight = rect.height;
        
        // Aplicar clamp para que no se salga del viewport
        newX = Math.max(MARGIN, Math.min(newX, window.innerWidth - buttonWidth - MARGIN));
        newY = Math.max(MARGIN, Math.min(newY, window.innerHeight - buttonHeight - MARGIN));
        
        // Actualizar posición final del botón
        const finalPos = {
          x: newX,
          y: newY
        };
        
        setPosition(finalPos);
        chatPositionRef.current = finalPos;
        
        // Si el chat está abierto, actualizar posición del panel también
        if (isOpen) {
          calculatePanelPosition(finalPos);
        }
        
        // Limpiar transform
        chatRef.current.style.transform = '';
      }
      
      isDraggingRef.current = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      
      // Limpiar hasDraggedRef después de un pequeño delay
      setTimeout(() => {
        hasDraggedRef.current = false;
      }, 100);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Click handler separado para abrir/cerrar
  const handleToggleChat = (e: React.MouseEvent) => {
    // Solo toggle si NO hubo drag
    if (!hasDraggedRef.current) {
      e.preventDefault();
      e.stopPropagation();
      
      // Al abrir, calcular posición del panel
      if (!isOpen) {
        calculatePanelPosition(position);
      }
      
      setIsOpen(!isOpen);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      text: inputMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };
    
    addMessage(userMessage);
    setInputMessage('');
    setIsTyping(true);
    
    try {
      // Preparar el historial para enviar al backend
      const conversationHistory = [...messages, userMessage].map(msg => ({
        role: msg.sender === 'user' ? 'user' : 'assistant',
        content: msg.text
      }));

      // Llamar al endpoint del backend
      const response = await api.post('/assistant/chat', {
        messages: conversationHistory
      });

      if (response.data.success) {
        const aionResponse: ChatMessage = {
          id: (Date.now() + 1).toString(),
          text: response.data.response,
          sender: 'aion',
          timestamp: new Date()
        };
        
        addMessage(aionResponse);
      } else {
        throw new Error(response.data.error || 'Error desconocido');
      }
    } catch (error: any) {
      console.error('Error al obtener respuesta del asistente:', error);
      
      const errorResponse: ChatMessage = {
        id: (Date.now() + 1).toString(),
        text: 'Disculpá, tuve un problema técnico. Por favor, intentá de nuevo.',
        sender: 'aion',
        timestamp: new Date()
      };
      
      addMessage(errorResponse);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ════════════════════════════════════════════════════════════════
  // RENDER: Botón minimizado
  // ════════════════════════════════════════════════════════════════
  
  if (!isOpen) {
    return (
      <div
        ref={chatRef}
        style={{
          position: 'fixed',
          left: `${position.x}px`,
          top: `${position.y}px`,
          zIndex: 9999,
          pointerEvents: 'auto',
          willChange: 'transform'
        }}
      >
        <button
          onMouseDown={handleHeaderMouseDown}
          onClick={handleToggleChat}
          style={{
            cursor: 'grab'
          }}
          className="bg-sky-600/90 hover:bg-sky-600 backdrop-blur-sm text-white p-3 rounded-full shadow-lg transition-all duration-300 flex items-center space-x-2 active:cursor-grabbing"
        >
          <span className="text-lg">🤖</span>
          <span className="hidden md:block font-medium text-sm">AION</span>
        </button>
      </div>
    );
  }
  
  // ════════════════════════════════════════════════════════════════
  // RENDER: Chatbot expandido
  // ════════════════════════════════════════════════════════════════
  
  return (
    <div 
      ref={chatRef}
      style={{
        position: 'fixed',
        left: `${panelPosition.x}px`,
        top: `${panelPosition.y}px`,
        zIndex: 9999,
        pointerEvents: 'auto',
        width: '384px',
        height: '500px',
        willChange: 'transform'
      }}
      className="bg-slate-800/95 backdrop-blur-md border border-slate-700 rounded-xl shadow-2xl flex flex-col"
    >
      {/* Header - DRAGGABLE */}
      <div 
        className="chat-header flex items-center justify-between p-3 border-b border-slate-700 bg-sky-600/90 backdrop-blur-sm rounded-t-xl active:cursor-grabbing"
        onMouseDown={handleHeaderMouseDown}
        style={{
          cursor: 'grab', // Grab estático
          userSelect: 'none'
        }}
      >
        <div className="flex items-center space-x-2">
          <div className="w-7 h-7 bg-sky-100 rounded-full flex items-center justify-center">
            <span className="text-sky-700 font-bold text-xs">AI</span>
          </div>
          <div>
            <h3 className="text-white font-semibold text-sm">AION Assistant</h3>
            <p className="text-sky-100 text-xs opacity-80">Asistente BI</p>
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation(); // No interferir con drag
            setIsOpen(false);
          }}
          className="text-sky-100 hover:text-white transition-colors"
        >
          <span className="text-xl">✕</span>
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] p-3 rounded-lg ${
                message.sender === 'user'
                  ? 'bg-sky-600 text-white'
                  : 'bg-slate-700 text-slate-200'
              }`}
            >
              <p className="text-sm leading-relaxed">{message.text}</p>
              <p className={`text-xs mt-1 ${
                message.sender === 'user' ? 'text-sky-100' : 'text-slate-400'
              }`}>
                {message.timestamp.toLocaleTimeString('es-ES', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
        ))}
        
        {isTyping && (
          <div className="flex justify-start">
            <div className="bg-slate-700 text-slate-200 p-3 rounded-lg">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                <div className="w-2 h-2 bg-sky-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-slate-700">
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Pregunta sobre AION BI..."
            className="flex-1 bg-slate-700 text-white p-2 rounded-lg border border-slate-600 focus:border-sky-500 focus:outline-none text-sm"
            disabled={isTyping}
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isTyping}
            className="bg-sky-600 hover:bg-sky-700 disabled:opacity-50 disabled:cursor-not-allowed text-white p-2 rounded-lg transition-colors"
          >
            <span className="text-sm">➤</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AionAssistant;
